import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useMsal } from "@azure/msal-react";
import { useLoading } from "../components/LoadingContext";
import axiosInstance from "../util/axiosInstance";
import { checkSession } from "../util/checkSession";
import { useSurveyContext } from "../context/SurveyContext";
import SuccessPopup from "./SuccessPopup"; 
import { API_KEY } from "../config";

interface IQuestion {
  partitionKey: string;
  rowKey: string;
  riskFactor: string;
  values: string;
}


const SelfReportTool: React.FC = () => {
  const { setLoading } = useLoading();
  const [selfReport, setSelfReport] = useState<IQuestion | null>(null);
  const [workLocation, setWorkLocation] = useState<IQuestion | null>(null);
  const [fitForWork, setFitForWork] = useState("");
  const [selected, setSelected] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { surveyData, setSurveyData } = useSurveyContext();
   const { instance, accounts } = useMsal();
   const [showPopup, setShowPopup] = useState(false);
   const [riskScore, setRiskScore] = useState<number | null>(null);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [selfRes, workRes] = await Promise.all([
          axiosInstance.get(`/GetMasterData/SelfReportTool?code=${API_KEY}`),
          axiosInstance.get(`/GetMasterData/WorkLocation?code=${API_KEY}`),
        ]);
        setSelfReport(selfRes.data[0]);
        setWorkLocation(workRes.data[0]);
      } catch {
        // handle errors
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading]);

  const handleSelect = (key: string, value: string) => {
    setSelected((prev) => ({ ...prev, [key]: value }));
  };

   const handleClosePopUp = async () => {
     setShowPopup(false);
     setSurveyData({} as typeof surveyData);
     navigate("/welcome");
   }

 const handleSubmit = async () => {
  checkSession();

  const SelfReportTool = {
    [selfReport!.rowKey]: selected[selfReport!.rowKey],
    [workLocation!.rowKey]: selected[workLocation!.rowKey],
    fitForWork,
  };

  // Instead of setting state and using surveyData right away, merge locally
  const updatedSurveyData = {
    ...surveyData,
    SelfReportTool
  };
  // Update context with the new survey data
  setSurveyData(updatedSurveyData);

  try {
      setLoading(true);
      const email = accounts[0]?.username || "";;

      if (!email) {
        alert("Email is required.");
        setLoading(false);
        return;
      }

      const payload = {
        email: email,
        data: updatedSurveyData,
      };

      const response = await axiosInstance.post(`/GenerateSurveyReport?code=${API_KEY}`, payload);

      setLoading(false);
      setRiskScore(response.data.totalRiskScore); // store score for popup
      setShowPopup(true); // show the popup
    } catch (error) {
      console.error("Error fetching risk score:", error);
      setLoading(false);
    }
  };


  if (!selfReport || !workLocation) return <Container />;

  const selfOptions = JSON.parse(JSON.parse(selfReport.values));
  const workOptions = JSON.parse(JSON.parse(workLocation.values));

  const allFilled =
    selected[selfReport.rowKey] &&
    selected[workLocation.rowKey] &&
    fitForWork;
  const photo = selfReport.partitionKey?.split(":")[0] || "";

  return (
    <>
    <Container>
       <Logo src={`${photo}.png`} alt={`${photo} Logo`} />
      <Title>{selfReport.partitionKey}</Title>
      <Form>
        <FieldSet>
          <QuestionText>{selfReport.riskFactor}</QuestionText>
          <SelectWrapper>
            <Select
              value={selected[selfReport.rowKey] || ""}
              onChange={(e) => handleSelect(selfReport.rowKey, e.target.value)}
            >
              <option value="">Select</option>
              {selfOptions.map((opt: string) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </SelectWrapper>
        </FieldSet>

        <FieldSet>
          <QuestionText> {workLocation.partitionKey.split(":")[1]}</QuestionText>
          <SelectWrapper>
            <Select
              value={selected[workLocation.rowKey] || ""}
              onChange={(e) => handleSelect(workLocation.rowKey, e.target.value)}
            >
              <option value="">Select</option>
              {workOptions.map((opt: string) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </SelectWrapper>
        </FieldSet>

        <FieldSet>
          <QuestionText>General Wellness : Do you feel fit for work today?</QuestionText>
          <SelectWrapper>
            <Select
              value={fitForWork}
              onChange={(e) => setFitForWork(e.target.value)}
            >
              <option value="">Select</option>
              <option value="YES">YES</option>
              <option value="NO">NO</option>
            </Select>
          </SelectWrapper>
        </FieldSet>

     <DisclaimerText>
  In submitting this form, you understand that WSP New Zealand Limited will handle your personal information in accordance with the{" "}
  <StyledLink
    href="https://canopy.wsp.com/sites/privacy-gl/documents/162548/Internal-Privacy-Notice-"
    target="_blank"
    rel="noopener noreferrer"
  >
    Internal Privacy Notice
  </StyledLink>{" "}
  and{" "}
  <StyledLink
    href="https://canopy.wsp.com/sites/privacy-gl/documents/95771/WSP-Privacy-Policy"
    target="_blank"
    rel="noopener noreferrer"
  >
    WSP Privacy Policy
  </StyledLink>
  .
</DisclaimerText>


    <Button type="button" disabled={!allFilled} onClick={handleSubmit}>
      Submit
    </Button>
      </Form>
    </Container>
    {showPopup && (
      <SuccessPopup
        totalRiskScore={riskScore}
        show={showPopup}
        onClose={() => handleClosePopUp()}
      />
    )}
  </>
    
  );
};

export default SelfReportTool;


// Styled Components

const StyledLink = styled.a`
  color: #0077cc;
  text-decoration: underline;
  &:hover {
    color: #005fa3;
  }
`;

const DisclaimerText = styled.p`
  font-size: 14px;
  color: #ccc;
  margin-top: 20px;
  text-align: center;
  line-height: 1.4;
`;


const Logo = styled.img`
  width: 120px;
  margin-bottom: 10px;
`;

const Container = styled.div`
  background-color: #000;
  color: white;
  padding: 20px;
  min-height: 78.5vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  color: #FF372F;
  margin: 10px 0;
`;

const Form = styled.form`
  width: 100%;
  max-width: 600px;
  margin-top: 20px;
`;

const FieldSet = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  color: #FF372F;
  font-weight: bold;
  display: block;
  margin-bottom: 5px;
`;

const QuestionText = styled.p`
  margin-bottom: 10px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 4px;
  border: 2px solid #CCC;
  appearance: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
`;

const SelectWrapper = styled.div`
  position: relative;

  &::after {
    content: "▼";
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    color: #888;
    pointer-events: none;
  }
`;


const Button = styled.button`
  background-color: #FF372F;
  color: white;
  padding: 12px 20px;
  font-size: 16px;
  border: none;
  border-radius: 3px;
  margin-top: 30px;
  width: 100%;
  cursor: pointer;

  &:disabled {
    background-color: #888;
    cursor: not-allowed;
  }
`;
